import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import {
	IItemForGetShortInfo,
	TFileId,
	IFileData,
	TLanguageIDs,
	IGetCustomerInfo,
	IUpdateCustomer,
	ICustomerLanguage,
	CardBrands,
	TUseTipsByDefault,
} from 'types';
import { addIdMapper } from 'utils/helpers';
import {
	EditCustomerContext,
	editCustomerInitialState,
	TEditCustomerSelectedClientType,
	TEditCustomerVisibleForm,
	TTypeOfEditCustomerModalWindow,
	IFormMccCard,
} from './editCustomerContext';
import { editCustomerReducer } from './editCustomerReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditCustomerActionTypes } from './editCustomerActions';
import { getCustomerInfo } from 'backend/customers';
import { useFetchData } from 'hooks/useFetchData';

const EditCustomerState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editCustomerReducer, editCustomerInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const customerID = params.get('customerID');

	const { response, loading, refetch } = useFetchData<IGetCustomerInfo, string>(
		customerID ? getCustomerInfo : null,
		customerID || '',
	);

	useNamedHeader(response?.data.customer.name);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditCustomerActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			const tips: Nullable<number>[] = [];
			let defaultTips: TUseTipsByDefault = null;
			let countOfTips = 0;
			while (countOfTips !== 4) {
				tips.push(
					response.data.customer.defaultTips[countOfTips]
						? response.data.customer.defaultTips[countOfTips].value
						: null,
				);
				if (
					response.data.customer.defaultTips?.[countOfTips] &&
					response.data.customer.defaultTips[countOfTips].isDefault
				) {
					defaultTips = String(countOfTips) as TUseTipsByDefault;
				}
				countOfTips++;
			}

			const getTags = (languages: ICustomerLanguage[]) => {
				return languages.map((el) => ({
					languageId: el.languageId,
					tags: [],
				}));
			};

			dispatch({
				type: EditCustomerActionTypes.SET_ALL_DATA,
				payload: {
					filePath: response.data.customer.filePath,
					customerId: response.data.customer.id,
					name: response.data.customer.name || '',
					email: response.data.customer.email || '',
					emailBodyTemplate: response.data.customer.emailBodyTemplate || '',
					clientType: response.data.customer.clientType,
					billTemplateFileData: {
						fileId: '',
						fileHash: null,
						fileName: null,
					},
					billTemplateFilePath: response.data.customer.billTemplatePath,
					billAdvertisingImageData: {
						fileId: '',
						fileHash: null,
						fileName: null,
					},
					billAdvertisingImage: response.data.customer.billAdvertisingImage,
					billAdvertisingLink: response.data.customer.billAdvertisingLink,
					priceType: response.data.customer.priceType,
					tips0: tips[0],
					tips1: tips[1],
					tips2: tips[2],
					tips3: tips[3],
					defaultTips,
					isShowLoyaltyProgramButtons: response.data.customer.isShowLoyaltyProgramButtons,
					baseMcc: response.data.customer.merchantCategoryCode?.baseMcc
						? response.data.customer.merchantCategoryCode.baseMcc
						: null,
					mainMccCardBrand: response.data.customer.merchantCategoryCode?.mccCards[0]?.brand || null,
					mainMccCardMcc: response.data.customer.merchantCategoryCode?.mccCards[0]?.mcc
						? response.data.customer.merchantCategoryCode.mccCards[0].mcc
						: null,
					mccCards: response.data.customer.merchantCategoryCode?.mccCards.slice(1).map(addIdMapper) || [],
					vivaWalletCredentialsId: response.data.customer.vivaWalletCredentialsId,
					ads: String(response.data.customer.ads),
					menuVersion: response.data.customer.menuVersion || '',
					contentVersion: response.data.customer.contentVersion || '',
					mixpanelToken: response.data.customer.mixpanelToken || '',
					stopListSyncIntervalMinutes: response.data.customer.stopListSyncIntervalMinutes,
					IDSelectedCustomerDefaultLanguage: response.data.customer.defaultLanguageId,
					IDsSelectedCustomerLanguages: response.data.customer.languages.map((el) => el.languageId),
					customerLanguages: response.data.customer.languages,
					tags: response.data.customer.tags.length
						? response.data.customer.tags.map((el) => ({ languageId: el.languageId, tags: el.tags }))
						: getTags(response.data.customer.languages),
					isActiveBpa: response.data.customer.isActiveBpa,
				},
			});
			dispatch({
				type: EditCustomerActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.customer.name || '',
						email: response.data.customer.email || '',
						emailBodyTemplate: response.data.customer.emailBodyTemplate || '',
						clientType: response.data.customer.clientType,
						billTemplateFileData: {
							fileId: '',
							fileHash: null,
							fileName: null,
						},
						billAdvertisingImageData: {
							fileId: '',
							fileHash: null,
							fileName: null,
						},
						billAdvertisingImage: response.data.customer.billAdvertisingImage,
						billAdvertisingLink: response.data.customer.billAdvertisingLink,
						priceType: response.data.customer.priceType,
						tips0: tips[0],
						tips1: tips[1],
						tips2: tips[2],
						tips3: tips[3],
						defaultTips,
						isShowLoyaltyProgramButtons: response.data.customer.isShowLoyaltyProgramButtons,
						baseMcc: response.data.customer.merchantCategoryCode?.baseMcc
							? response.data.customer.merchantCategoryCode.baseMcc
							: null,
						mainMccCardBrand: response.data.customer.merchantCategoryCode?.mccCards[0]?.brand || null,
						mainMccCardMcc: response.data.customer.merchantCategoryCode?.mccCards[0]?.mcc
							? response.data.customer.merchantCategoryCode.mccCards[0].mcc
							: null,
						mccCards: response.data.customer.merchantCategoryCode?.mccCards.slice(1) || [],
						vivaWalletCredentialsId: response.data.customer.vivaWalletCredentialsId,
						ads: String(response.data.customer.ads),
						menuVersion: response.data.customer.menuVersion || '',
						contentVersion: response.data.customer.contentVersion || '',
						mixpanelToken: response.data.customer.mixpanelToken || '',
						stopListSyncIntervalMinutes: response.data.customer.stopListSyncIntervalMinutes,
						IDSelectedCustomerDefaultLanguage: response.data.customer.defaultLanguageId,
						IDsSelectedCustomerLanguages: response.data.customer.languages.map((el) => el.languageId),
						filePath: response.data.customer.filePath,
						tags: response.data.customer.tags.length
							? response.data.customer.tags.map((el) => ({ languageId: el.languageId, tags: el.tags }))
							: getTags(response.data.customer.languages),
						isActiveBpa: response.data.customer.isActiveBpa,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditCustomerActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditCustomerVisibleForm) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: Nullable<string>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditCustomerActionTypes.DELETE_PHOTO }), []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changeEmailBodyTemplate = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_EMAIL_BODY_TEMPLATE,
			payload: {
				emailBodyTemplate: value,
			},
		});
	}, []);

	const setClientType = useCallback((value: TEditCustomerSelectedClientType) => {
		dispatch({
			type: EditCustomerActionTypes.SET_CLIENT_TYPE,
			payload: {
				clientType: value,
			},
		});
	}, []);

	const changeBillTemplateFileData = useCallback((value: IFileData) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_BILL_TEMPLATE_FILE_DATA,
			payload: {
				billTemplateFileData: value,
			},
		});
	}, []);

	const changeBillAdvertisingImageData = useCallback((value: IFileData) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_BILL_ADVERTISING_IMAGE_DATA,
			payload: {
				billAdvertisingImageData: value,
			},
		});
	}, []);

	const changeBillAdvertisingLink = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_BILL_ADVERTISING_LINK,
			payload: {
				billAdvertisingLink: value,
			},
		});
	}, []);

	const deleteBillAdvertisingImage = useCallback(
		() => dispatch({ type: EditCustomerActionTypes.DELETE_BILL_ADVERTISING_IMAGE }),
		[],
	);

	const changePriceType = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_PRICE_TYPE,
			payload: {
				priceType: value,
			},
		});
	}, []);

	const setTips0 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_TIPS_0,
			payload: {
				tips0: value,
			},
		});
	}, []);

	const setTips1 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_TIPS_1,
			payload: {
				tips1: value,
			},
		});
	}, []);

	const setTips2 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_TIPS_2,
			payload: {
				tips2: value,
			},
		});
	}, []);

	const setTips3 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_TIPS_3,
			payload: {
				tips3: value,
			},
		});
	}, []);

	const setDefaultTips = useCallback(
		(value: TUseTipsByDefault) => {
			dispatch({
				type: EditCustomerActionTypes.SET_DEFAULT_TIPS,
				payload: {
					defaultTips: state.defaultTips === value ? null : value,
				},
			});
		},
		[state.defaultTips],
	);

	const toggleShowLoyaltyProgramButtons = useCallback(() => {
		dispatch({ type: EditCustomerActionTypes.TOGGLE_SHOW_LOYALTY_PROGRAM_BUTTONS });
	}, []);

	const changeBaseMcc = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_BASE_MCC,
			payload: {
				baseMcc: value,
			},
		});
	}, []);

	const addMccCard = useCallback(
		(value: IFormMccCard) => {
			const updatedMccCards = [...state.mccCards, value];

			dispatch({
				type: EditCustomerActionTypes.CHANGE_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const deleteMccCard = useCallback(
		(id: string) => {
			const updatedMccCards = state.mccCards.filter((el) => el.id !== id);

			dispatch({
				type: EditCustomerActionTypes.CHANGE_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const changeMainMccCardBrand = useCallback((value: CardBrands) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_MAIN_MCC_CARD_BRAND,
			payload: {
				mainMccCardBrand: value,
			},
		});
	}, []);

	const changeMccCardBrand = useCallback(
		(id: string, value: CardBrands) => {
			const updatedMccCards = state.mccCards.map((el) => (el.id === id ? { ...el, brand: value } : el));

			dispatch({
				type: EditCustomerActionTypes.CHANGE_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const changeMainMccCardMcc = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_MAIN_MCC_CARD_MCC,
			payload: {
				mainMccCardMcc: value,
			},
		});
	}, []);

	const changeMccCardMcc = useCallback(
		(id: string, value: Nullable<number>) => {
			const updatedMccCards = state.mccCards.map((el) => (el.id === id ? { ...el, mcc: value } : el));

			dispatch({
				type: EditCustomerActionTypes.CHANGE_MCC_CARD,
				payload: {
					mccCards: updatedMccCards,
				},
			});
		},
		[state.mccCards],
	);

	const setVivaWalletCredentialsId = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_VIVA_WALLET_CREDENTIALS_ID,
			payload: {
				vivaWalletCredentialsId: value,
			},
		});
	}, []);

	const setAds = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.SET_ADS,
			payload: {
				ads: value,
			},
		});
	}, []);

	const changeMenuVersion = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_MENU_VERSION,
			payload: {
				menuVersion: value,
			},
		});
	}, []);

	const changeContentVersion = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_CONTENT_VERSION,
			payload: {
				contentVersion: value,
			},
		});
	}, []);

	const setMixpanelToken = useCallback((value: string) => {
		dispatch({
			type: EditCustomerActionTypes.SET_MIXPANEL_TOKEN,
			payload: {
				mixpanelToken: value,
			},
		});
	}, []);

	const setStopListSyncIntervalMinutes = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_STOP_LIST_SYNC_INTERVAL_MINUTES,
			payload: {
				stopListSyncIntervalMinutes: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditCustomerActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				fetchedLanguages: value,
			},
		});
	}, []);

	const setCustomerDefaultLanguage = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_ID_CUSTOMER_DEFAULT_LANGUAGE,
			payload: {
				IDSelectedCustomerDefaultLanguage: value,
			},
		});
	}, []);

	const setCustomerLanguages = useCallback(
		(value: string[]) => {
			if (value.length > state.IDsSelectedCustomerLanguages.length) {
				const newLanguageId = value.find((el) => !state.IDsSelectedCustomerLanguages.includes(el));
				dispatch({
					type: EditCustomerActionTypes.SET_IDs_CUSTOMER_LANGUAGES,
					payload: {
						IDsSelectedCustomerLanguages: value,
						tags: newLanguageId
							? [
									...state.tags,
									{
										languageId: newLanguageId as TLanguageIDs,
										tags: state.tags.length ? state.tags[0].tags.map(() => '') : [],
									},
							  ]
							: state.tags,
					},
				});
			} else {
				const updatedTags = state.tags.filter((el) => value.includes(el.languageId));
				dispatch({
					type: EditCustomerActionTypes.SET_IDs_CUSTOMER_LANGUAGES,
					payload: {
						IDsSelectedCustomerLanguages: value,
						tags: updatedTags,
					},
				});
			}
		},
		[state.IDsSelectedCustomerLanguages, state.tags],
	);

	const addTags = useCallback(() => {
		const updatedTags = state.tags.map((el) => ({ ...el, tags: [...el.tags, ''] }));
		dispatch({
			type: EditCustomerActionTypes.CHANGE_TAGS,
			payload: {
				tags: updatedTags,
			},
		});
	}, [state.tags]);

	const changeTags = useCallback(
		(value: string, item: number, languageId: string) => {
			const updatedTags = state.tags.map((el) => {
				if (el.languageId !== languageId) {
					return el;
				}
				const updatedItemOfTags = [...el.tags];
				updatedItemOfTags[item] = value;
				return { ...el, tags: updatedItemOfTags };
			});
			dispatch({
				type: EditCustomerActionTypes.CHANGE_TAGS,
				payload: {
					tags: updatedTags,
				},
			});
		},
		[state.tags],
	);

	const deleteTag = useCallback(
		(item: number) => {
			const updatedTags = state.tags.map((el) => {
				const updatedItemsOfTags = el.tags.filter((tag, i) => i !== item);
				return { ...el, tags: updatedItemsOfTags };
			});
			dispatch({
				type: EditCustomerActionTypes.CHANGE_TAGS,
				payload: {
					tags: updatedTags,
				},
			});
		},
		[state.tags],
	);

	const toggleActivateBPA = useCallback(() => {
		dispatch({ type: EditCustomerActionTypes.TOGGLE_ACTIVATE_BPA });
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateCustomer>) => {
		dispatch({
			type: EditCustomerActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditCustomerModalWindow) => {
		dispatch({
			type: EditCustomerActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditCustomerActionTypes.RESET_FIELDS }), []);

	return (
		<EditCustomerContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFile,
				deletePhoto,
				changeName,
				changeEmail,
				changeEmailBodyTemplate,
				setClientType,
				changeBillTemplateFileData,
				changeBillAdvertisingImageData,
				changeBillAdvertisingLink,
				deleteBillAdvertisingImage,
				changePriceType,
				setTips0,
				setTips1,
				setTips2,
				setTips3,
				setDefaultTips,
				toggleShowLoyaltyProgramButtons,
				changeBaseMcc,
				addMccCard,
				deleteMccCard,
				changeMainMccCardBrand,
				changeMccCardBrand,
				changeMainMccCardMcc,
				changeMccCardMcc,
				setVivaWalletCredentialsId,
				setAds,
				changeMenuVersion,
				changeContentVersion,
				setMixpanelToken,
				setStopListSyncIntervalMinutes,
				setFetchedLanguages,
				setCustomerDefaultLanguage,
				setCustomerLanguages,
				addTags,
				changeTags,
				deleteTag,
				toggleActivateBPA,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				visibleForm: state.visibleForm,
				customerId: state.customerId,
				filePath: state.filePath,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				name: state.name,
				email: state.email,
				emailBodyTemplate: state.emailBodyTemplate,
				clientType: state.clientType,
				billTemplateFileData: state.billTemplateFileData,
				billTemplateFilePath: state.billTemplateFilePath,
				billAdvertisingImageData: state.billAdvertisingImageData,
				billAdvertisingImage: state.billAdvertisingImage,
				billAdvertisingLink: state.billAdvertisingLink,
				priceType: state.priceType,
				tips0: state.tips0,
				tips1: state.tips1,
				tips2: state.tips2,
				tips3: state.tips3,
				defaultTips: state.defaultTips,
				isShowLoyaltyProgramButtons: state.isShowLoyaltyProgramButtons,
				baseMcc: state.baseMcc,
				mainMccCardBrand: state.mainMccCardBrand,
				mainMccCardMcc: state.mainMccCardMcc,
				mccCards: state.mccCards,
				vivaWalletCredentialsId: state.vivaWalletCredentialsId,
				ads: state.ads,
				menuVersion: state.menuVersion,
				contentVersion: state.contentVersion,
				mixpanelToken: state.mixpanelToken,
				stopListSyncIntervalMinutes: state.stopListSyncIntervalMinutes,
				fetchedLanguages: state.fetchedLanguages,
				IDSelectedCustomerDefaultLanguage: state.IDSelectedCustomerDefaultLanguage,
				IDsSelectedCustomerLanguages: state.IDsSelectedCustomerLanguages,
				customerLanguages: state.customerLanguages,
				tags: state.tags,
				isActiveBpa: state.isActiveBpa,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditCustomerContext.Provider>
	);
};

export default EditCustomerState;
